/**
 * Push expansion work-order phase/subphase to matching AGOL segment features.
 * Matches on the feature `work_order` attribute; updates `phase` + `subphase`.
 *
 * Edits target the configured segments Feature Layer portal item
 * (REACT_APP_ARCGIS_EXPANSION_FEATURE_LAYER_ID / EXPANSION_SEGMENTS_FEATURE_LAYER_ITEM_ID).
 */
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import Graphic from "@arcgis/core/Graphic.js";
import Portal from "@arcgis/core/portal/Portal.js";
import PortalItem from "@arcgis/core/portal/PortalItem.js";
import {
  configureArcgisApiKey,
  getArcgisApiKey,
  getArcgisPortalUrl,
  loadResource,
  MAP_LOAD_MS,
  withTimeout,
} from "./arcgisMapUtils";
import { formatSubphaseLabel } from "./commercialSubphases";
import {
  EXPANSION_WORK_ORDER_FIELD,
  getExpansionSegmentsFeatureLayerItemId,
  loadExpansionSegmentWebMap,
} from "./expansionMapConfig";
import {
  buildWorkOrderWhereClause,
  resolveSegmentLayers,
} from "./expansionSegmentMapUtils";
import { normalizeExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";
import { currentSubphaseSlugForRow, isCvinExpansion } from "../projectGridColumns";
import { displayPhaseForRow } from "../projectPhaseDisplay";

export const AGOL_PHASE_FIELD = "phase";
export const AGOL_SUBPHASE_FIELD = "subphase";

function escapeArcgisSqlString(value) {
  return String(value ?? "").replace(/'/g, "''");
}

/** Exact WO match, then padded display form (9573.3 → 9573.30). */
export function buildExactWorkOrderWhereClause(workOrder, field = EXPANSION_WORK_ORDER_FIELD) {
  const wo = String(workOrder ?? "").trim();
  if (!wo) return null;
  const col = String(field || EXPANSION_WORK_ORDER_FIELD).trim();
  const variants = [];
  const push = (v) => {
    const t = String(v ?? "").trim();
    if (t && !variants.includes(t)) variants.push(t);
  };
  push(wo);
  push(normalizeExpansionWorkOrderDisplay(wo));
  if (!variants.length) return null;
  if (variants.length === 1) {
    return `${col} = '${escapeArcgisSqlString(variants[0])}'`;
  }
  return `(${variants.map((v) => `${col} = '${escapeArcgisSqlString(v)}'`).join(" OR ")})`;
}

export function expansionPhaseSubphaseForAgol(row) {
  if (!row || !isCvinExpansion(row)) return null;
  const workOrder =
    normalizeExpansionWorkOrderDisplay(
      row?.acf_groups_json?.subsegment_summary?.work_order ?? row?.work_order
    ) || String(row?.work_order ?? "").trim();
  if (!workOrder) return null;
  const phase = displayPhaseForRow(row).trim();
  const slug = currentSubphaseSlugForRow(row);
  const subphase = slug ? formatSubphaseLabel(slug) : "";
  return { workOrder, phase, subphase, subphaseSlug: slug || "" };
}

function resolveObjectIdField(layer) {
  const fromLayer = String(layer?.objectIdField || "").trim();
  if (fromLayer) return fromLayer;
  const fields = Array.isArray(layer?.fields) ? layer.fields : [];
  const oid = fields.find((f) => String(f?.type || "").toLowerCase() === "oid");
  if (oid?.name) return String(oid.name);
  return "OBJECTID";
}

function resolveAttrField(layer, preferred) {
  const want = String(preferred || "").trim().toLowerCase();
  if (!want) return preferred;
  const fields = Array.isArray(layer?.fields) ? layer.fields : [];
  const hit = fields.find((f) => String(f?.name || "").trim().toLowerCase() === want);
  return hit?.name || preferred;
}

function layerServiceKey(layer) {
  const url = String(layer?.url || "").replace(/\/$/, "").toLowerCase();
  const layerId = layer?.layerId;
  if (url && layerId != null && Number.isFinite(Number(layerId))) {
    return `${url}/${layerId}`;
  }
  return url || String(layer?.title || layer?.id || Math.random());
}

/**
 * Load the editable segments FeatureLayer from its portal item id.
 */
export async function loadExpansionSegmentsFeatureLayer(apiKey) {
  const itemId = getExpansionSegmentsFeatureLayerItemId();
  if (!itemId) return null;
  const key = (apiKey || getArcgisApiKey() || "").trim();
  configureArcgisApiKey(key);

  const portalCandidates = [getArcgisPortalUrl(), "https://www.arcgis.com"].filter(
    (u, i, arr) => u && arr.indexOf(u) === i
  );
  let lastError = null;

  for (const portalUrl of portalCandidates) {
    try {
      const portal = new Portal({ url: portalUrl });
      if (key) portal.apiKey = key;
      await withTimeout(loadResource(portal), MAP_LOAD_MS, "ArcGIS portal");

      const portalItem = new PortalItem({ id: itemId, portal });
      if (key) portalItem.apiKey = key;
      await withTimeout(loadResource(portalItem), MAP_LOAD_MS, "ArcGIS feature layer item");

      const layer = new FeatureLayer({ portalItem });
      await withTimeout(loadResource(layer), MAP_LOAD_MS, "ArcGIS feature layer");
      return layer;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error(`Could not load feature layer item ${itemId}`);
}

/**
 * Prefer the portal feature layer item; fall back to web-map layer discovery.
 */
export async function resolveEditableSegmentLayers(apiKey) {
  try {
    const layer = await loadExpansionSegmentsFeatureLayer(apiKey);
    if (layer) return [layer];
  } catch (err) {
    console.warn(
      "[agol phase sync] feature layer item load failed, falling back to web map:",
      err?.message || err
    );
  }

  const map = await loadExpansionSegmentWebMap(apiKey);
  await loadResource(map);
  const layers = await resolveSegmentLayers(map);
  const byKey = new Map();
  for (const layer of layers) {
    await loadResource(layer).catch(() => null);
    const key = layerServiceKey(layer);
    if (!byKey.has(key)) byKey.set(key, layer);
  }
  return [...byKey.values()];
}

async function queryFeaturesForUpdate(layer, where, outFields) {
  if (!layer || !where || typeof layer.queryFeatures !== "function") return [];
  await loadResource(layer);
  const result = await layer.queryFeatures({
    where,
    returnGeometry: false,
    outFields,
  });
  return result?.features || [];
}

/**
 * Update AGOL features matching work_order with phase + subphase display values.
 * @returns {Promise<{ synced: boolean, updated: number, skipped?: string, error?: string }>}
 */
export async function syncExpansionWorkOrderPhaseToAgol({
  workOrder,
  phase,
  subphase,
  apiKey = getArcgisApiKey(),
} = {}) {
  const wo = String(workOrder ?? "").trim();
  if (!wo) return { synced: false, updated: 0, skipped: "missing_work_order" };
  const key = (apiKey || "").trim();
  if (!key) return { synced: false, updated: 0, skipped: "missing_api_key" };

  configureArcgisApiKey(key);
  const layers = await resolveEditableSegmentLayers(key);
  if (!layers.length) {
    return { synced: false, updated: 0, skipped: "no_segment_layer" };
  }

  const where = buildExactWorkOrderWhereClause(wo) || buildWorkOrderWhereClause(wo);
  if (!where) return { synced: false, updated: 0, skipped: "bad_where" };

  let updated = 0;
  const errors = [];

  for (const layer of layers) {
    const oidField = resolveObjectIdField(layer);
    const phaseField = resolveAttrField(layer, AGOL_PHASE_FIELD);
    const subphaseField = resolveAttrField(layer, AGOL_SUBPHASE_FIELD);
    const woField = resolveAttrField(layer, EXPANSION_WORK_ORDER_FIELD);

    let features = [];
    try {
      features = await queryFeaturesForUpdate(layer, where, [
        oidField,
        woField,
        phaseField,
        subphaseField,
      ]);
    } catch (err) {
      errors.push(err?.message || String(err));
      continue;
    }
    if (!features.length) continue;

    const graphics = [];
    for (const feature of features) {
      const attrs = feature?.attributes || {};
      const oid = attrs[oidField];
      if (oid == null) continue;
      const nextPhase = phase != null ? String(phase) : "";
      const nextSub = subphase != null ? String(subphase) : "";
      const prevPhase = attrs[phaseField] != null ? String(attrs[phaseField]) : "";
      const prevSub = attrs[subphaseField] != null ? String(attrs[subphaseField]) : "";
      if (prevPhase === nextPhase && prevSub === nextSub) continue;
      graphics.push(
        new Graphic({
          attributes: {
            [oidField]: oid,
            [phaseField]: nextPhase,
            [subphaseField]: nextSub,
          },
        })
      );
    }
    if (!graphics.length) continue;

    if (typeof layer.applyEdits !== "function") {
      errors.push(`Layer ${layer.title || layer.url} does not support applyEdits`);
      continue;
    }
    try {
      const editResult = await layer.applyEdits({ updateFeatures: graphics });
      const results = editResult?.updateFeatureResults || editResult?.updateResults || [];
      for (const r of results) {
        if (r?.error) {
          errors.push(r.error?.message || String(r.error));
        } else {
          updated += 1;
        }
      }
      if (!results.length) updated += graphics.length;
    } catch (err) {
      errors.push(err?.message || String(err));
    }
  }

  if (updated === 0 && errors.length) {
    return { synced: false, updated: 0, error: errors.join("; ") };
  }
  if (updated === 0) {
    return { synced: false, updated: 0, skipped: "no_matching_features" };
  }
  const layerUrl = layers[0]?.url ? String(layers[0].url) : undefined;
  if (layerUrl) {
    console.info(
      "[agol phase sync] updated",
      wo,
      `phase=${phase}`,
      `subphase=${subphase}`,
      `(${updated} feature(s); layer ${layerUrl})`
    );
  }
  return {
    synced: true,
    updated,
    layerUrl,
    featureLayerItemId: getExpansionSegmentsFeatureLayerItemId(),
    error: errors.length ? errors.join("; ") : undefined,
  };
}

/**
 * Fire-and-forget AGOL sync when expansion phase/subphase changed after a PATCH.
 * Never throws; logs warnings on failure so board saves stay reliable.
 */
export function maybeSyncExpansionPhaseToAgol(beforeRow, afterRow) {
  try {
    if (!afterRow || !isCvinExpansion(afterRow)) return null;
    const next = expansionPhaseSubphaseForAgol(afterRow);
    if (!next) return null;
    const prev = beforeRow && isCvinExpansion(beforeRow) ? expansionPhaseSubphaseForAgol(beforeRow) : null;
    if (
      prev &&
      prev.phase === next.phase &&
      prev.subphase === next.subphase &&
      prev.workOrder === next.workOrder
    ) {
      return null;
    }
    const promise = syncExpansionWorkOrderPhaseToAgol(next)
      .then((result) => {
        if (result?.error) {
          console.warn("[agol phase sync]", next.workOrder, result);
        } else if (result?.skipped && result.skipped !== "no_matching_features") {
          console.warn("[agol phase sync skipped]", next.workOrder, result.skipped);
        }
        return result;
      })
      .catch((err) => {
        console.warn("[agol phase sync]", next.workOrder, err?.message || err);
        return { synced: false, updated: 0, error: err?.message || String(err) };
      });
    return promise;
  } catch (err) {
    console.warn("[agol phase sync]", err?.message || err);
    return null;
  }
}
